import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { NavBar } from "../../components/NavBar";
import { api } from "../../api/api";
import { DateTime } from "luxon";
import { Toast } from "../../utils/Toast";
import { maskCurrency, maskPhone } from "../../utils/utils";
import { GrAnnounce } from "react-icons/gr";
import { FiSave, FiTrash2, FiUsers, FiZap, FiEdit2 } from "react-icons/fi";
import "./style.css";
import "../admin.css";

const initialForm = {
  idEmpresa: "",
  name: "",
  message: "",
  incluirLink: false,
  image: "",
  status: "scheduled",
  executionTime: "",
  payload: {},
};

const initialFilters = {
  inactiveForDays: "",
  activeInLastDays: "",
  lastOrderMoreThan: "",
  lastOrderLessThan: "",
  selectionMode: "balanced",
  limit: 100,
};

const filterPresets = [
  { id: "all", label: "Todos" },
  { id: "inactive-30", label: "Inativos 30 dias", mode: "inactive", days: "30" },
  { id: "inactive-60", label: "Inativos 60 dias", mode: "inactive", days: "60" },
  { id: "inactive-90", label: "Inativos 90 dias", mode: "inactive", days: "90" },
  { id: "recent-30", label: "Compraram nos últimos 30 dias", mode: "recent", days: "30" },
  { id: "custom", label: "Personalizado", custom: true },
];

const selectionStrategies = [
  {
    value: "balanced",
    label: "Balancear audiência",
    description: "Prioriza novos contatos e completa a audiência quando necessário.",
  },
  {
    value: "avoid_recent",
    label: "Somente novos",
    description: "Evita clientes usados recentemente em outras campanhas.",
  },
  {
    value: "allow_repeat",
    label: "Permitir repetidos",
    description: "Permite reutilizar clientes de campanhas anteriores.",
  },
];

function campaignIncludesLink(value) {
  return value === true || value === 1 || value === '1' || value === 'true';
}

function canEditCampaign(campaign) {
  return ['draft', 'scheduled'].includes(campaign?.status);
}

function parseCampaignDate(value) {
  const date = DateTime.fromISO(value || '', { zone: 'utc' });

  return date.isValid ? date : DateTime.fromSQL(value || '', { zone: 'utc' });
}

function getRequestFilters(filters) {
  const params = {
    selectionMode: filters.selectionMode,
    limit: filters.limit,
  };

  if (filters.inactiveForDays !== "") params.inactiveForDays = filters.inactiveForDays;
  if (filters.activeInLastDays !== "") {
    params.activeInLastDays = filters.activeInLastDays;
    // A API anterior lê inactiveForDays mesmo no modo recente.
    params.inactiveForDays = filters.activeInLastDays;
  }
  if (filters.lastOrderMoreThan !== "") params.lastOrderMoreThan = filters.lastOrderMoreThan;
  if (filters.lastOrderLessThan !== "") params.lastOrderLessThan = filters.lastOrderLessThan;

  return params;
}

export default function CampanhasPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const [campanhas, setCampanhas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loadingCampanhas, setLoadingCampanhas] = useState(true);
  const [campanhasError, setCampanhasError] = useState("");
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [clientesError, setClientesError] = useState("");
  const [improvingMessage, setImprovingMessage] = useState(false);
  const [saving, setSaving] = useState(false);
  const customerRequest = useRef(0);
  const savingRequest = useRef(false);
  const imageInput = useRef(null);

  const [form, setForm] = useState(initialForm);
  const [filters, setFilters] = useState(initialFilters);
  const [selectedPreset, setSelectedPreset] = useState("all");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadCampanhas();
  }, []);

  const loadCampanhas = async () => {
    try {
      setLoadingCampanhas(true);
      setCampanhasError("");
      const { data } = await api.get("/campanha");
      setCampanhas(data);
    } catch (error) {
      setCampanhasError("Não foi possível carregar as campanhas.");
    } finally {
      setLoadingCampanhas(false);
    }
  };

  useEffect(() => {
    if (!editId) return;
    let cancelled = false;
    api.get(`/campanha/${encodeURIComponent(editId)}`).then(({ data }) => {
      if (!cancelled) openCampaign(data);
    }).catch(() => Toast.warning("Não foi possível abrir a campanha para revisão."));
    return () => { cancelled = true; };
  }, [editId]);

  function openCampaign(campaign) {
    if (!canEditCampaign(campaign)) {
      Toast.warning('Campanhas inativas ou em execução não podem ser editadas.');

      return;
    }

    let payload = campaign.payload;

    if (typeof payload === 'string') {
      try {
        payload = JSON.parse(payload);
      } catch {
        Toast.warning('Não foi possível abrir a audiência desta campanha.');

        return;
      }
    }

    customerRequest.current += 1;
    const customers = Array.isArray(payload?.customers) ? payload.customers : [];
    const executionDate = parseCampaignDate(campaign.executionTime);

    setForm({
      ...initialForm,
      ...campaign,
      incluirLink: campaignIncludesLink(campaign.incluirLink),
      image: campaign.image || '',
      payload: { ...payload, customers },
      executionTime: campaign.status !== 'draft' && executionDate.isValid
        ? executionDate.toLocal().toFormat("yyyy-MM-dd'T'HH:mm")
        : '',
    });
    setClientes(customers);
    setFilters(initialFilters);
    setSelectedPreset('custom');
    setAdvancedOpen(false);
    setPreviewReady(true);
    setLoadingClientes(false);
    setClientesError('');
    setCurrentPage(1);

    if (imageInput.current) {
      imageInput.current.value = '';
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const handleChange = (e) => {
    if (e.target.name === "incluirLink") {
      setForm({ ...form, [e.target.name]: e.target.checked });
      return;
    }

    if (e.target.name === "image") {
      const file = e.target.files?.[0];

      if (!file) {
        setForm({ ...form, image: "" });
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const base64Data = reader.result.split(",")[1];
        setForm((prev) => ({ ...prev, image: base64Data }));
      };
      reader.readAsDataURL(file);
      return;
    }

    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFilterChange = (e) => {
    updateFilter(e.target.name, e.target.value);
  };

  const updateFilter = (name, value) => {
    customerRequest.current += 1;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "inactiveForDays" ? { activeInLastDays: "" } : {}),
      ...(name === "activeInLastDays" ? { inactiveForDays: "" } : {}),
    }));
    setSelectedPreset("custom");
    setClientes([]);
    setClientesError("");
    setLoadingClientes(false);
    setPreviewReady(false);
    setCurrentPage(1);
    setForm((prev) => ({ ...prev, payload: {} }));
  };

  const applyPreset = (preset) => {
    customerRequest.current += 1;
    setFilters({
      ...initialFilters,
      inactiveForDays: preset.mode === "inactive" ? preset.days : "",
      activeInLastDays: preset.mode === "recent" ? preset.days : "",
    });
    setSelectedPreset(preset.id);
    setAdvancedOpen(Boolean(preset.custom));
    setClientes([]);
    setClientesError("");
    setLoadingClientes(false);
    setPreviewReady(false);
    setCurrentPage(1);
    setForm((prev) => ({ ...prev, payload: {} }));
  };

  const activityMode = filters.inactiveForDays !== ""
    ? "inactive"
    : filters.activeInLastDays !== ""
      ? "recent"
      : "all";

  const updateActivityMode = (mode) => {
    customerRequest.current += 1;
    setFilters((prev) => ({
      ...prev,
      inactiveForDays: mode === "inactive" ? (prev.inactiveForDays || "30") : "",
      activeInLastDays: mode === "recent" ? (prev.activeInLastDays || "30") : "",
    }));
    setSelectedPreset("custom");
    setClientes([]);
    setClientesError("");
    setLoadingClientes(false);
    setPreviewReady(false);
    setCurrentPage(1);
    setForm((prev) => ({ ...prev, payload: {} }));
  };

  const buscarClientes = async () => {
    const requestId = ++customerRequest.current;
    const requestFilters = getRequestFilters(filters);

    if (
      filters.lastOrderMoreThan !== "" &&
      filters.lastOrderLessThan !== "" &&
      Number(filters.lastOrderMoreThan) > Number(filters.lastOrderLessThan)
    ) {
      Toast.warning("O valor mínimo do pedido não pode ser maior que o máximo.");
      return;
    }

    try {
      setLoadingClientes(true);
      setClientesError("");

      const { data } = await api.get("/customers/find-by-campaign", { params: requestFilters });

      if (requestId !== customerRequest.current) return;

      setClientes(data);
      setPreviewReady(true);
      setCurrentPage(1);
      setForm((prev) => ({
        ...prev,
        payload: {
          filters: requestFilters,
          customers: data,
          phones: data.map((c) => c.phone),
        },
      }));
    } catch (error) {
      if (requestId !== customerRequest.current) return;

      setClientes([]);
      setPreviewReady(false);
      setForm((prev) => ({ ...prev, payload: {} }));
      setClientesError("Não foi possível buscar os clientes. Tente novamente.");
    } finally {
      if (requestId === customerRequest.current) setLoadingClientes(false);
    }
  };

  const salvarCampanha = async (status = "scheduled") => {
    if (savingRequest.current) return;

    if (form.id && !canEditCampaign(form)) {
      Toast.warning('Campanhas inativas ou em execução não podem ser editadas.');

      return;
    }

    if (!form.name.trim()) {
      Toast.warning("Preencha o nome da campanha.");
      return;
    }

    if (!form.message.trim()) {
      Toast.warning("Preencha a mensagem da campanha.");
      return;
    }

    const executionDate = DateTime.fromFormat(
      form.executionTime,
      "yyyy-MM-dd'T'HH:mm"
    );

    if (status === "scheduled" && !executionDate.isValid) {
      Toast.warning("Data de execução inválida.");
      return;
    }

    if (status === "scheduled" && executionDate <= DateTime.local()) {
      Toast.warning("A data de execução deve ser futura.");
      return;
    }

    if (clientes.length === 0) {
      Toast.warning("Busque e selecione pelo menos um cliente.");
      return;
    }

    const minOrder = filters.lastOrderMoreThan;
    const maxOrder = filters.lastOrderLessThan;
    if (minOrder !== "" && maxOrder !== "" && Number(minOrder) > Number(maxOrder)) {
      Toast.warning("O valor mínimo do pedido não pode ser maior que o máximo.");
      return;
    }

    try {
      savingRequest.current = true;
      setSaving(true);
      const campaignData = {
        name: form.name.trim(),
        message: form.message.trim(),
        status,
        executionTime: executionDate.isValid ? executionDate.toISO() : DateTime.utc().toISO(),
        incluirLink: campaignIncludesLink(form.incluirLink),
        image: form.image || null,
        payload: { ...form.payload, customers: clientes },
      };
      if (form.id) {
        await api.put(`/campanha/${encodeURIComponent(form.id)}`, campaignData);
      } else {
        await api.post("/campanha", campaignData);
      }
      resetCampaign();
      Toast.success(
        status === 'draft'
          ? 'Rascunho salvo. Nenhuma mensagem será enviada.'
          : status === 'scheduled'
            ? 'Campanha agendada com sucesso.'
            : 'Alterações da campanha salvas.',
        3000,
      );
      await loadCampanhas();
    } catch (error) {
      if (!error?.response?.data?.message) {
        Toast.warning('Não foi possível salvar a campanha. Tente novamente.', 3000);
      }
    } finally {
      savingRequest.current = false;
      setSaving(false);
    }
  };

  function resetCampaign() {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);

      next.delete('edit');

      return next;
    }, { replace: true });
    customerRequest.current += 1;
    setForm(initialForm);
    setFilters(initialFilters);
    setClientes([]);
    setClientesError("");
    setSelectedPreset("all");
    setAdvancedOpen(false);
    setPreviewReady(false);
    setCurrentPage(1);
    setLoadingClientes(false);
    if (imageInput.current) imageInput.current.value = "";
  }

  function removeCustomer(selectedCustomer) {
    const customers = clientes.filter((customer) => customer !== selectedCustomer);
    const totalPages = Math.max(1, Math.ceil(customers.length / 10));

    setClientes(customers);
    setCurrentPage((page) => Math.min(page, totalPages));
    setForm((prev) => ({
      ...prev,
      payload: {
        ...prev.payload,
        customers,
        phones: customers.map((customer) => customer.phone),
      },
    }));
  }

  function formatCustomerDate(value) {
    if (!value) return "Sem data";

    const date = DateTime.fromISO(value);
    return date.isValid ? date.toLocal().toFormat("dd/MM/yyyy HH:mm") : "Sem data";
  }

  const totalPreviewPages = Math.max(1, Math.ceil(clientes.length / 10));
  const previewCustomers = clientes.slice((currentPage - 1) * 10, currentPage * 10);

  const melhorarMensagemComIA = async () => {
    if (!form.message?.trim()) {
      Toast.warning("Preencha a mensagem antes de melhorar com IA.");
      return;
    }

    try {
      setImprovingMessage(true);

      const { data } = await api.post("/campanha/improve-message", {
        message: form.message,
      });

      const improvedMessage =
        data?.message || data?.improvedMessage || data?.text || data;

      if (typeof improvedMessage === "string" && improvedMessage.trim()) {
        setForm((prev) => ({ ...prev, message: improvedMessage }));
        Toast.success("Mensagem melhorada com sucesso.");
        return;
      }

      Toast.warning("Não foi possível obter uma mensagem melhorada.");
    } catch (error) {
      Toast.warning("Erro ao melhorar mensagem com IA.");
    } finally {
      setImprovingMessage(false);
    }
  };

  function formatDateTimeLocal(dateTime) {
    const date = parseCampaignDate(dateTime);

    return date.isValid ? date.toLocal().toFormat('dd/MM/yyyy HH:mm') : 'Não informada';
  }

  function formatStatus(status) {
    switch (status) {
      case "draft":
        return "Rascunho";
      case "active":
        return "Ativa";
      case "inactive":
        return "Inativa";
      case "scheduled":
        return "Agendada";
      default:
        return 'Não informado';
    }
  }

  return (
    <NavBar>
      <main className="admin-page campaign-page">
        <header className="admin-page-header"><div><h1>Campanhas</h1><p>Crie comunicações segmentadas e acompanhe os resultados</p></div><GrAnnounce size={21} /></header>

        <section className="admin-surface campaign-form-section">
          <div className="admin-surface-header"><div><h2>{form.id ? "Editar campanha" : "Nova campanha"}</h2><p>Configure a mensagem e o agendamento do envio</p></div>{form.id ? (
            <button type="button" className="admin-secondary-button" onClick={resetCampaign} disabled={saving}>
              Cancelar edição
            </button>
          ) : <GrAnnounce size={19} />}</div>
          <div className="campaign-form-body">

          <div className="campaign-helper mb-3">
            <p className="campaign-helper-title mb-1">Personalização disponível</p>
            Use <code>@NOME</code> para inserir o nome do cliente automaticamente.
          </div>

          <div className="row">
            <div className="col-md-4 mb-2">
              <label htmlFor="campaign-name">Nome</label>
              <input
                className="form-control"
                id="campaign-name"
                name="name"
                value={form.name}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-4 mb-2">
              <label htmlFor="campaign-status">Status</label>
              <select
                className="form-control"
                id="campaign-status"
                name="status"
                value={form.status}
                disabled={true}
                onChange={handleChange}
              >
                <option value="draft">Rascunho</option>
                <option value="active">Ativa</option>
                <option value="inactive">Inativa</option>
                <option value="scheduled">Agendada</option>
              </select>
            </div>

            <div className="col-md-4 mb-2">
              <label htmlFor="campaign-execution">Data de execução</label>
              <input
                type="datetime-local"
                className="form-control"
                id="campaign-execution"
                name="executionTime"
                value={form.executionTime}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-12 mb-2">
              <label htmlFor="campaign-message">Mensagem</label>
              <textarea
                className="form-control campaign-textarea"
                id="campaign-message"
                name="message"
                rows={3}
                value={form.message}
                disabled={improvingMessage}
                onChange={handleChange}
              />

              <div>
                <small className="text-muted">
                  Use <code>@NOME</code> para inserir o nome do cliente na mensagem.
                </small>
              </div>
              <button
                className="campaign-ai-button mt-2"
                type="button"
                onClick={melhorarMensagemComIA}
                disabled={improvingMessage}
              >
                <FiZap /> {improvingMessage ? "Melhorando..." : "Melhorar mensagem com IA"}
              </button>
            </div>

            <div className="col-md-6 mb-2">
              <label htmlFor="campaign-image">Imagem</label>
              <input
                ref={imageInput}
                type="file"
                accept="image/jpeg"
                className="form-control"
                id="campaign-image"
                name="image"
                onChange={handleChange}
              />
              {form.image && (
                <div className="campaign-saved-image">
                  <img
                    src={/^(https?:|data:image\/)/.test(form.image) ? form.image : `data:image/jpeg;base64,${form.image}`}
                    alt="Imagem da campanha"
                  />
                  <button type="button" className="admin-secondary-button" onClick={() => {
                    setForm((current) => ({ ...current, image: '' }));

                    if (imageInput.current) {
                      imageInput.current.value = '';
                    }
                  }} disabled={saving}>
                    Remover imagem
                  </button>
                </div>
              )}
            </div>

            <div className="col-md-6 mb-2 d-flex align-items-end">
              <div className="form-check campaign-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="incluirLink"
                  name="incluirLink"
                  checked={form.incluirLink}
                  onChange={handleChange}
                />
                <label className="form-check-label" htmlFor="incluirLink">
                  Incluir link do cardapio?
                </label>
              </div>
            </div>
          </div>
          </div>
        </section>

        <section className="admin-surface campaign-audience-section">
          <div className="admin-surface-header campaign-filter-heading">
            <div>
              <h2>Escolha sua audiência</h2>
              <p>Use um atalho ou refine a seleção nos filtros avançados</p>
            </div>
            <button
              type="button"
              className="campaign-advanced-toggle"
              aria-expanded={advancedOpen}
              onClick={() => setAdvancedOpen((open) => !open)}
            >
              {advancedOpen ? "Ocultar filtros avançados" : "Filtros avançados"}
            </button>
          </div>
          <div className="campaign-audience-body">

          <div className="campaign-presets" aria-label="Atalhos de audiência">
            {filterPresets.map((preset) => (
              <button
                type="button"
                key={preset.id}
                className={`campaign-preset ${selectedPreset === preset.id ? "is-active" : ""}`}
                aria-pressed={selectedPreset === preset.id}
                onClick={() => applyPreset(preset)}
              >
                {preset.label}
              </button>
            ))}
          </div>

          {advancedOpen && (
            <div className="campaign-advanced">
              <fieldset className="campaign-fieldset">
                <legend>Atividade de compra</legend>
                <div className="campaign-activity-options">
                  {[
                    ["all", "Todos"],
                    ["inactive", "Inativos"],
                    ["recent", "Recentes"],
                  ].map(([value, label]) => (
                    <label className="campaign-activity-option" key={value}>
                      <input
                        type="radio"
                        name="activityMode"
                        value={value}
                        checked={activityMode === value}
                        onChange={() => updateActivityMode(value)}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <div className="campaign-filter-grid">
                {activityMode !== "all" && (
                  <div>
                    <label htmlFor="campaign-activity-days">Dias</label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      className="form-control"
                      id="campaign-activity-days"
                      name={activityMode === "inactive" ? "inactiveForDays" : "activeInLastDays"}
                      value={activityMode === "inactive" ? filters.inactiveForDays : filters.activeInLastDays}
                      onChange={handleFilterChange}
                    />
                  </div>
                )}
                <div>
                  <label htmlFor="lastOrderMoreThan">Valor mínimo (R$)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control"
                    id="lastOrderMoreThan"
                    name="lastOrderMoreThan"
                    value={filters.lastOrderMoreThan}
                    onChange={handleFilterChange}
                    placeholder="Sem mínimo"
                  />
                </div>
                <div>
                  <label htmlFor="lastOrderLessThan">Valor máximo (R$)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control"
                    id="lastOrderLessThan"
                    name="lastOrderLessThan"
                    value={filters.lastOrderLessThan}
                    onChange={handleFilterChange}
                    placeholder="Sem máximo"
                  />
                </div>
                <div>
                  <label htmlFor="campaign-limit">Limite de clientes</label>
                  <select
                    className="form-control"
                    id="campaign-limit"
                    name="limit"
                    value={filters.limit}
                    onChange={(event) => updateFilter("limit", Number(event.target.value))}
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>

              <fieldset className="campaign-fieldset campaign-strategy-fieldset">
                <legend>Estratégia de repetição</legend>
                <div className="campaign-strategies">
                  {selectionStrategies.map((strategy) => (
                    <label
                      className={`campaign-strategy ${filters.selectionMode === strategy.value ? "is-active" : ""}`}
                      key={strategy.value}
                    >
                      <input
                        type="radio"
                        name="selectionMode"
                        value={strategy.value}
                        checked={filters.selectionMode === strategy.value}
                        onChange={handleFilterChange}
                      />
                      <span>
                        <strong>{strategy.label}</strong>
                        <small>{strategy.description}</small>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
            </div>
          )}

          <div className="campaign-search-row">
            <button
              type="button"
              className="admin-primary-button campaign-search-button"
              onClick={buscarClientes}
              disabled={loadingClientes}
            >
              {loadingClientes ? "Buscando audiência..." : "Buscar audiência e visualizar prévia"}
            </button>
          </div>

          {previewReady && clientes.length === 0 && (
            <div className="campaign-preview-empty" role="status">
              <strong>Nenhum cliente encontrado</strong>
              <span>Tente ajustar os filtros para ampliar a audiência.</span>
            </div>
          )}
          {clientesError && (
            <div className="alert alert-danger mt-3 mb-0">{clientesError}</div>
          )}

          {previewReady && clientes.length > 0 && (
            <section className="campaign-preview" aria-label="Prévia da audiência">
              <div className="campaign-preview-header">
                <div>
                  <h6>Prévia da audiência</h6>
                  <p>{clientes.length} {clientes.length === 1 ? "cliente selecionado" : "clientes selecionados"}</p>
                </div>
                <span>Página {currentPage} de {totalPreviewPages}</span>
              </div>

              <div className="admin-table-wrap">
                <table className="admin-table campaign-preview-table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Telefone</th>
                      <th>Último pedido</th>
                      <th>Valor</th>
                      <th>Remover</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewCustomers.map((customer) => (
                      <tr key={customer.id || customer.phone}>
                        <td data-label="Nome"><strong>{customer.name || "Nome não informado"}</strong></td>
                        <td data-label="Telefone">{customer.phone ? maskPhone(String(customer.phone)) : "Não informado"}</td>
                        <td data-label="Último pedido">
                          <span className="campaign-last-order">
                            {formatCustomerDate(customer.lastOrderAt || customer.lastOrder)}
                          </span>
                        </td>
                        <td data-label="Valor">
                          {customer.lastOrderTotal !== null && customer.lastOrderTotal !== undefined
                            ? maskCurrency(customer.lastOrderTotal)
                            : customer.total !== null && customer.total !== undefined
                              ? maskCurrency(customer.total)
                            : "Não informado"}
                        </td>
                        <td data-label="Remover">
                          <button
                            type="button"
                            className="admin-icon-button danger campaign-remove-customer"
                            onClick={() => removeCustomer(customer)}
                            aria-label={`Remover ${customer.name || "cliente"} da audiência`}
                          >
                            <FiTrash2 />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPreviewPages > 1 && (
                <div className="campaign-pagination" aria-label="Paginação da prévia">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((page) => page - 1)}
                  >
                    Anterior
                  </button>
                  <span>{currentPage} / {totalPreviewPages}</span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={currentPage === totalPreviewPages}
                    onClick={() => setCurrentPage((page) => page + 1)}
                  >
                    Próxima
                  </button>
                </div>
              )}
            </section>
          )}
          </div>
        </section>

        <div className="campaign-save-bar">
          <div><strong>Revise antes de enviar</strong><span>Confira a mensagem, descontos sugeridos e público. O rascunho não envia mensagens.</span></div>
          {form.id && form.status === 'scheduled' && (
            <button type="button" className="admin-primary-button" onClick={() => salvarCampanha(form.status)} disabled={saving}>
              <FiSave /> {saving ? 'Salvando...' : 'Salvar alterações'}
            </button>
          )}
          <button type="button" className="admin-secondary-button" onClick={() => salvarCampanha('draft')} disabled={saving}>
            <FiSave /> Salvar rascunho
          </button>
          {(!form.id || form.status === 'draft') && (
            <button type="button" className="admin-primary-button" onClick={() => salvarCampanha('scheduled')} disabled={saving}>
              <FiSave /> {saving ? 'Salvando...' : 'Salvar e agendar'}
            </button>
          )}
        </div>

        <section className="admin-surface campaign-history-section">
          <div className="admin-surface-header"><div><h2>Campanhas cadastradas</h2><p>{campanhas.length} campanhas encontradas</p></div><FiUsers size={19} /></div>

          {loadingCampanhas && <div className="admin-loading"><div className="spinner-border" role="status"><span className="visually-hidden">Carregando...</span></div></div>}
          {campanhasError && (
            <div className="alert alert-danger d-flex justify-content-between align-items-center">
              <span>{campanhasError}</span>
              <button className="btn btn-sm btn-outline-danger" onClick={loadCampanhas}>Tentar novamente</button>
            </div>
          )}
          {!loadingCampanhas && !campanhasError && campanhas.length === 0 && (
            <div className="admin-empty"><GrAnnounce size={25} /><strong>Nenhuma campanha cadastrada</strong><span>Crie uma campanha para iniciar seus envios.</span></div>
          )}

          {!loadingCampanhas && !campanhasError && campanhas.length > 0 && <div className="admin-table-wrap">
            <table className="admin-table campaign-table">
              <thead className="thead-light">
                <tr>
                  <th>Nome</th>
                  <th>Status</th>
                  <th>Execução</th>
                  <th>Clientes</th>
                  <th>Clientes que recusaram</th>
                  <th>Vendas feitas pela campanha</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {campanhas.map((c) => (
                  <tr key={c.id}>
                    <td data-label="Nome">
                      {canEditCampaign(c) ? (
                        <button type="button" className="campaign-name-button" onClick={() => openCampaign(c)} disabled={saving}>
                          {c.name}
                        </button>
                      ) : <strong>{c.name}</strong>}
                    </td>
                    <td data-label="Status"><span className={`admin-status ${c.status === 'active' ? 'active' : c.status === 'inactive' ? 'inactive' : 'scheduled'}`}>{formatStatus(c.status)}</span></td>
                    <td data-label="Execução">{c.status === "draft" ? "Sem agendamento" : formatDateTimeLocal(c.executionTime)}</td>
                    <td data-label="Clientes">{c.payload?.customers?.length || 0}</td>
                    <td data-label="Recusaram">{c.countUnsubscribes || 0}</td>
                    <td data-label="Vendas">{c.countSelled || 0}</td>
                    <td data-label="Ações">
                      {canEditCampaign(c) && (
                        <button type="button" className="admin-secondary-button campaign-edit-button" onClick={() => openCampaign(c)} disabled={saving} aria-label={`Editar campanha ${c.name}`}>
                          <FiEdit2 /> Editar
                        </button>
                      )}
                      <button
                        className="admin-icon-button danger"
                        onClick={async () => {
                          const confirm = await Toast.confirm("Tem certeza que deseja excluir esta campanha?")
                          if (confirm) {
                            await api.delete(`/campanha/${c.id}`);
                            await loadCampanhas();

                          }
                        }}
                      >
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
        </section>
      </main>
    </NavBar>
  );
}
